import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { AuditEntryDto, Page } from '@zenarty/shared';
import { History } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api';
import { actionIcon, actionLabel, actorLabel, Diff } from '../components/audit';
import { EmptyState, ErrorMessage, KeyValue, PageHeader } from '../components/kit';
import { Drawer } from '../components/overlays';
import { DataTable, type Column } from '../components/table';
import { formatDateTime, timeAgo, titleCase } from '../lib/format';

const ENTITIES = ['site', 'client', 'user', 'device', 'session', 'operator'];
const RANGES = [
  ['', 'All time'],
  ['1', 'Last 24 hours'],
  ['7', 'Last 7 days'],
  ['30', 'Last 30 days'],
] as const;
const PAGE = 50;

export function AuditPage() {
  const [entityType, setEntityType] = useState('');
  const [days, setDays] = useState('');
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState<AuditEntryDto | null>(null);

  const params = new URLSearchParams({ limit: String(PAGE), offset: String(page * PAGE) });
  if (entityType) params.set('entityType', entityType);
  if (days) params.set('from', new Date(Date.now() - Number(days) * 86_400_000).toISOString());
  const q = useQuery({
    queryKey: ['audit', 'list', entityType, days, page],
    queryFn: () => api<Page<AuditEntryDto>>(`/audit-log?${params.toString()}`),
    placeholderData: keepPreviousData,
  });

  const columns: Column<AuditEntryDto>[] = [
    {
      id: 'when',
      header: 'When',
      cell: (e) => (
        <div>
          <div className="nowrap">{timeAgo(e.createdAt)}</div>
          <div className="sub nowrap">{formatDateTime(e.createdAt)}</div>
        </div>
      ),
    },
    {
      id: 'action',
      header: 'Event',
      cell: (e) => {
        const Icon = actionIcon(e.action);
        const alarm = e.action === 'auth.failed_attempt' || e.action === 'auth.refresh_token_reuse';
        return (
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <span
              className={`icon-tile ${alarm ? 'danger' : ''}`}
              style={{ width: 30, height: 30 }}
            >
              <Icon size={15} />
            </span>
            <span className="primary-cell">{actionLabel(e.action)}</span>
          </div>
        );
      },
    },
    { id: 'actor', header: 'By', cell: (e) => actorLabel(e) },
    {
      id: 'entity',
      header: 'Record',
      cell: (e) => <span className="tag">{titleCase(e.entityType)}</span>,
    },
    {
      id: 'ip',
      header: 'IP address',
      cell: (e) => <span className="mono muted">{e.ip ?? '—'}</span>,
    },
  ];

  const toolbar = (
    <>
      <select
        className="select"
        aria-label="Record type"
        value={entityType}
        onChange={(e) => {
          setEntityType(e.target.value);
          setPage(0);
        }}
      >
        <option value="">All records</option>
        {ENTITIES.map((t) => (
          <option key={t} value={t}>
            {titleCase(t)}s
          </option>
        ))}
      </select>
      <select
        className="select"
        aria-label="Time range"
        value={days}
        onChange={(e) => {
          setDays(e.target.value);
          setPage(0);
        }}
      >
        {RANGES.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </>
  );

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every sign-in, configuration change and security event, with who, when, where and exactly what changed. Entries can never be edited or deleted."
      />
      <ErrorMessage error={q.error} />
      <DataTable
        columns={columns}
        rows={q.data?.items ?? []}
        rowKey={(e) => e.id}
        loading={q.isLoading}
        toolbar={toolbar}
        onRowClick={setOpen}
        server={{
          total: q.data?.total ?? 0,
          page,
          pageSize: PAGE,
          onPage: setPage,
          search: '',
          onSearch: () => undefined,
        }}
        empty={<EmptyState icon={History} title="No events match these filters" />}
      />
      {open ? (
        <Drawer
          title={actionLabel(open.action)}
          subtitle={formatDateTime(open.createdAt)}
          onClose={() => setOpen(null)}
        >
          <section>
            <div className="section-title">Event</div>
            <KeyValue
              items={[
                ['By', `${actorLabel(open)} (${open.actorKind})`],
                [
                  'Record',
                  `${titleCase(open.entityType)}${open.entityId ? ` · ${open.entityId}` : ''}`,
                ],
                ['IP address', open.ip],
                ['Request ID', open.requestId ? <code key="r">{open.requestId}</code> : null],
                ['Event ID', <code key="e">{open.id}</code>],
              ]}
            />
          </section>
          {open.before || open.after ? (
            <section>
              <div className="section-title">What changed</div>
              <div className="diff" style={{ marginTop: 0 }}>
                <div className="diff-row" style={{ fontWeight: 600 }}>
                  <span>Field</span>
                  <span>Before</span>
                  <span>After</span>
                </div>
              </div>
              <Diff before={open.before} after={open.after} />
            </section>
          ) : null}
        </Drawer>
      ) : null}
    </>
  );
}
